import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, Globe, Info, PackageCheck, ArrowRight } from "lucide-react";
import Seo from "@/lib/Seo";
import { api, mediaUrl } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function OuTrouverLeJeu() {
  const [pos, setPos] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/public/points-of-sale").then((r) => setPos(r.data)).catch(() => {}),
      api.get("/public/pickup-locations").then((r) => setPickups(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo title="Où trouver le jeu" path="/ou-trouver-le-jeu"
        description="Trouvez Douzième Homme — Le Jeu en point de vente : boutique officielle OGC Nice, librairies et magasins partenaires à Nice et sur la Côte d'Azur." />

      <section className="py-14 sm:py-20" data-testid="pos-page">
        <div className="container-x">
          <Reveal className="max-w-3xl">
            <p className="eyebrow mb-4">Points de vente</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx">
              Où trouver le jeu ?
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Douzième Homme — Le Jeu est disponible chez nos revendeurs partenaires, à la boutique officielle
              OGC Nice et sur le site officiel du club.
            </p>
          </Reveal>

          {loading ? (
            <p className="mt-12 text-muted-foreground" data-testid="pos-loading">Chargement…</p>
          ) : (
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pos-grid">
              {pos.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <article className="h-full bg-white border border-line p-7 flex flex-col hover:border-rouge/40 transition-colors" data-testid={`pos-card-${i}`}>
                    {p.image && <img src={mediaUrl(p.image)} alt={p.name} className="h-16 w-auto object-contain mb-5 self-start" loading="lazy" />}
                    <h2 className="font-display text-xl font-bold text-onyx leading-snug">{p.name}</h2>
                    <div className="mt-4 space-y-2.5 text-sm text-muted-foreground flex-1">
                      {p.address && <p className="flex items-start gap-2"><MapPin className="w-4 h-4 text-rouge shrink-0 mt-0.5" /> {p.address}</p>}
                      {p.hours && <p className="flex items-start gap-2"><Clock className="w-4 h-4 text-rouge shrink-0 mt-0.5" /> {p.hours}</p>}
                      {p.phone && <p className="flex items-start gap-2"><Phone className="w-4 h-4 text-rouge shrink-0 mt-0.5" /> <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="hover:text-rouge">{p.phone}</a></p>}
                      {p.info && <p className="flex items-start gap-2"><Info className="w-4 h-4 text-rouge shrink-0 mt-0.5" /> {p.info}</p>}
                    </div>
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`pos-website-${i}`}
                        className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-rouge hover:text-rouge-hover transition-colors">
                        <Globe className="w-4 h-4" /> Visiter le site
                      </a>
                    )}
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {pickups.length > 0 && (
        <section className="py-14 sm:py-20 bg-sand border-t border-line" data-testid="pickup-section">
          <div className="container-x">
            <Reveal className="flex items-center gap-3 mb-3">
              <PackageCheck className="w-6 h-6 text-rouge" />
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase text-onyx">
                Retrait gratuit de vos commandes en ligne
              </h2>
            </Reveal>
            <Reveal>
              <p className="text-base text-muted-foreground max-w-2xl mb-10">
                Commandez sur le site et récupérez gratuitement votre jeu sous 24h chez l'un de nos partenaires de retrait.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pickup-grid">
              {pickups.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <div className="h-full bg-white border border-line p-6" data-testid={`pickup-card-${i}`}>
                    <MapPin className="w-5 h-5 text-rouge mb-3" />
                    <h3 className="font-display font-bold text-onyx">{p.name}</h3>
                    {p.address && <p className="text-sm text-muted-foreground mt-2">{p.address}</p>}
                    {p.hours && <p className="text-sm text-muted-foreground mt-1 flex items-start gap-1.5"><Clock className="w-3.5 h-3.5 text-rouge shrink-0 mt-0.5" /> {p.hours}</p>}
                    {p.phone && <p className="text-sm text-muted-foreground mt-1 flex items-start gap-1.5"><Phone className="w-3.5 h-3.5 text-rouge shrink-0 mt-0.5" /> {p.phone}</p>}
                    {p.info && <p className="text-xs text-muted-foreground mt-2">{p.info}</p>}
                    <p className="text-xs font-bold text-field mt-3 uppercase tracking-wide">Gratuit</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-10">
              <Link to="/acheter" data-testid="pickup-cta-acheter"
                className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
                Commander avec retrait gratuit <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </section>
      )}
    </>
  );
}
